import { useEffect } from 'react';

import { useGlobalMutation, useGlobalQuery } from 'state/query-client/hooks';
import {
  getAgentSoul,
  isSoulRouteMissing,
  soulErrorStatus,
  updateAgentSoul,
  type AgentSoul,
  type UpdateAgentSoulPayload,
  type UpdateAgentSoulResponse,
} from '../services/agent-soul-service';

export const AGENT_SOUL_QUERY_KEY = 'agent-soul';

let missingRouteLogged = false;

/** Hidden like a tenant without souls when the backend has no soul routes yet. */
export const isAgentSoulHidden = (soul: AgentSoul | undefined, error: unknown) =>
  soul?.soulsEnabled === false || isSoulRouteMissing(error);

/**
 * The soul an agent reads now. A refused or missing agent (4xx) is not asked again.
 *
 * @example
 * const { data, isLoading } = useAgentSoul(agentId);
 */
export function useAgentSoul(agentId?: string | null) {
  const query = useGlobalQuery<AgentSoul, unknown>({
    queryKey: [AGENT_SOUL_QUERY_KEY, agentId],
    queryFn: () => getAgentSoul(agentId as string),
    enabled: !!agentId,
    retry: (failureCount, error) => {
      const status = soulErrorStatus(error);
      return !(status && status >= 400 && status < 500) && failureCount < 1;
    },
  });
  const routeMissing = isSoulRouteMissing(query.error);

  useEffect(() => {
    if (!routeMissing || missingRouteLogged) return;
    missingRouteLogged = true;
    console.warn('The agent soul API is not available; the soul editor is hidden.');
  }, [routeMissing]);

  return query;
}

/** Saves, or with empty content removes, the agent's own soul at `expectedRevision`. */
export function useUpdateAgentSoul() {
  return useGlobalMutation<UpdateAgentSoulResponse, unknown, UpdateAgentSoulPayload>({
    mutationFn: updateAgentSoul,
  });
}
